import React, { useState } from 'react';
import { Plus, Edit, Trash2, Award, FileText, Users, Link, Copy, Check, UserCheck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { supabase } from '../lib/supabase';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const TrainerForm = ({ trainer, onClose }) => {
  const { addTrainer, updateTrainer } = useApp();
  const [form, setForm] = useState({
    name: trainer?.name || '',
    phone: trainer?.phone || '',
    specialty: trainer?.specialty || '',
    bio: trainer?.bio || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (trainer) updateTrainer(trainer.id, form);
    else addTrainer(form);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">이름</label>
          <input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="이름" required />
        </div>
        <div>
          <label className="label">연락처</label>
          <input className="input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="010-0000-0000" />
        </div>
        <div className="col-span-2">
          <label className="label">전문분야</label>
          <input className="input" value={form.specialty} onChange={e => setForm(p => ({ ...p, specialty: e.target.value }))} placeholder="체중감량, 근력증가 등" />
        </div>
        <div className="col-span-2">
          <label className="label">소개</label>
          <textarea className="input resize-none" rows={3} value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} placeholder="경력, 자격증 등..." />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary">{trainer ? '수정' : '등록'}</button>
      </div>
    </form>
  );
};

const Trainers = () => {
  const { trainers, deleteTrainer, ptAssignments, members, showToast } = useApp();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [inviteModal, setInviteModal] = useState(null); // { trainer, code }
  const [inviteLoading, setInviteLoading] = useState(null); // trainerId
  const [copied, setCopied] = useState('');

  async function handleGenerateInvite(trainer) {
    setInviteLoading(trainer.id);
    const { data, error } = await supabase.rpc('owner_generate_trainer_invite', { p_trainer_id: trainer.id });
    setInviteLoading(null);
    if (error) {
      showToast('초대 링크 발급에 실패했습니다. 다시 시도해주세요.', 'error');
      return;
    }
    setInviteModal({ trainer, code: data });
  }

  function handleCopy(text, key) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    }).catch(() => {});
  }

  const getTrainerStats = (trainerId) => {
    const assignments = ptAssignments.filter(p => p.trainerId === trainerId && p.status === 'active');
    return assignments.length;
  };

  const getTrainerMembers = (trainerId) => {
    const assignments = ptAssignments.filter(p => p.trainerId === trainerId);
    const memberIds = [...new Set(assignments.map(a => a.memberId))];
    return memberIds.map(id => members.find(m => m.id === id)).filter(Boolean);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setEditingTrainer(null); setIsFormOpen(true); }} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          트레이너 등록
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {trainers.length === 0 ? (
          <div className="col-span-3 card p-12 text-center text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>등록된 트레이너가 없습니다.</p>
          </div>
        ) : (
          trainers.map(trainer => {
            const activePtCount = getTrainerStats(trainer.id);
            const trainerMembers = getTrainerMembers(trainer.id);
            return (
              <div key={trainer.id} className="card p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-700 dark:text-blue-400">{trainer.name[0]}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{trainer.name}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{trainer.phone}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 items-center">
                    {trainer.userId ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-full">
                        <UserCheck className="w-3 h-3" /> 계정 있음
                      </span>
                    ) : (
                      <button
                        onClick={() => handleGenerateInvite(trainer)}
                        disabled={inviteLoading === trainer.id}
                        title="초대 링크 발급"
                        className="p-1.5 text-gray-400 hover:text-primary-500 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
                      >
                        {inviteLoading === trainer.id
                          ? <div className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                          : <Link className="w-4 h-4" />}
                      </button>
                    )}
                    <button onClick={() => { setEditingTrainer(trainer); setIsFormOpen(true); }} className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteConfirm(trainer)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {trainer.specialty && (
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">{trainer.specialty}</p>
                  </div>
                )}
                {trainer.bio && (
                  <div className="flex items-start gap-2 mb-3">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">{trainer.bio}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">활성 PT {activePtCount}건</span>
                  {trainerMembers.length > 0 && (
                    <button
                      onClick={() => setSelectedTrainer(selectedTrainer?.id === trainer.id ? null : trainer)}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      담당 회원 {trainerMembers.length}명
                    </button>
                  )}
                </div>

                {selectedTrainer?.id === trainer.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">담당 회원</p>
                    <div className="space-y-1">
                      {trainerMembers.map(m => (
                        <div key={m.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <span className="text-xs font-semibold text-primary-700 dark:text-primary-400">{m.name[0]}</span>
                          </div>
                          {m.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 초대 코드 모달 */}
      <Modal isOpen={!!inviteModal} onClose={() => { setInviteModal(null); setCopied(''); }} title="트레이너 초대 링크">
        {inviteModal && (() => {
          const inviteUrl = `${window.location.origin}/?trainer_code=${inviteModal.code}`;
          return (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-white">{inviteModal.trainer.name}</span> 트레이너에게 아래 링크 또는 코드를 전달해주세요.
              </p>
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <p className="text-xs text-gray-400 mb-1">초대 코드</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-mono font-bold tracking-widest text-primary-600 dark:text-primary-400">{inviteModal.code}</span>
                  <button onClick={() => handleCopy(inviteModal.code, 'code')} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors">
                    {copied === 'code' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <p className="text-xs text-gray-400 mb-1">초대 링크</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-600 dark:text-gray-300 truncate">{inviteUrl}</p>
                  <button onClick={() => handleCopy(inviteUrl, 'url')} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                    {copied === 'url' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center">코드는 7일간 유효하며 1회만 사용 가능합니다.</p>
            </div>
          );
        })()}
      </Modal>

      <Modal isOpen={isFormOpen} onClose={() => { setIsFormOpen(false); setEditingTrainer(null); }} title={editingTrainer ? '트레이너 수정' : '트레이너 등록'}>
        <TrainerForm trainer={editingTrainer} onClose={() => { setIsFormOpen(false); setEditingTrainer(null); }} />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="트레이너 삭제"
        message={`${deleteConfirm?.name} 트레이너를 삭제하시겠습니까?`}
        onConfirm={() => { deleteTrainer(deleteConfirm.id); setDeleteConfirm(null); }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default Trainers;
